import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  deleteIssuePlacements,
  listIssuePlacements,
  saveIssuePlacements,
  subscribeToIssuePlacements,
} from "@/stores/pm/repository";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import {
  changedPositions,
  indexPlacements,
  mergeArrangement,
  storedSequence,
  type BandRanks,
  type IssuePlacementRow,
  type PlacementLayout,
} from "@/domains/pm/utils/issuePlacement";

const EMPTY: IssuePlacementRow[] = [];

export interface IssuePlacements {
  /** The stored positions for one band, or undefined if nobody arranged it. */
  ranksFor: (groupKey: string) => BandRanks | undefined;
  /**
   * Store a band's new order, as the user now sees it. Applied to the cache at
   * once, so the rows settle where they were dropped instead of snapping back
   * for a round trip; undone with a toast if the write is refused.
   */
  saveBand: (groupKey: string, visibleOrder: number[]) => Promise<void>;
}

/**
 * The shared arrangement of one surface (board or Gantt) under one grouping.
 *
 * Read for the whole building, like dependencies: both surfaces render every
 * band at once. Kept live over realtime, because an arrangement somebody else
 * makes while you are looking at the chart is exactly what "shared" means —
 * and re-read on every re-join, because realtime has no replay.
 *
 * `mayArrangeIssue` decides which issues a save may write a row for. A band's
 * order is stored for issues the user never saw — the ones a filter hid, and
 * the ones that have since moved elsewhere — and RLS refuses the upsert whole,
 * so an unwritable row anywhere in the band fails the whole drop.
 */
export function useIssuePlacements(
  buildingId: number,
  layout: PlacementLayout,
  groupBy: string,
  mayArrangeIssue?: (issueId: number) => boolean
): IssuePlacements {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const enabled = Number.isFinite(buildingId);
  const queryKey = useMemo(
    () => ["pm-issue-placements", buildingId, layout, groupBy] as const,
    [buildingId, layout, groupBy]
  );

  const { data = EMPTY, error } = useQuery({
    queryKey,
    queryFn: () => listIssuePlacements(buildingId, layout, groupBy),
    enabled,
    staleTime: 30_000,
  });

  /**
   * A failed read is NOT "nobody has arranged this yet", but it renders as
   * exactly that: every surface falls back to its default order, silently. The
   * `pm_issue_placements` migration ships separately from this code, so a
   * missing table or a refused select is a real deployment state — it has to be
   * visible in production rather than looking like an empty arrangement.
   */
  useEffect(() => {
    if (!error) return;
    console.error("[pm] issue placements read failed", {
      buildingId,
      layout,
      groupBy,
      message: error instanceof Error ? error.message : String(error),
    });
    Sentry.captureException(error, {
      tags: { area: "pm", operation: "issue_placements_read", layout },
      extra: { buildingId, groupBy },
      fingerprint: ["pm", "issue-placements", "read", layout],
    });
  }, [error, buildingId, layout, groupBy]);

  /**
   * Saves still on the wire, and whether a change arrived meanwhile. A re-read
   * started while a save is in flight can return the band from before it and
   * paint over the optimistic order, so echoes wait for the saves to settle
   * and then re-read once.
   */
  const inFlight = useRef(0);
  const staleWhileSaving = useRef(false);
  // Read through a ref: the caller builds this predicate from the issues it is
  // rendering, so it is a fresh function on every render and would rebuild the
  // realtime subscription if it were a dependency.
  const mayArrangeIssueRef = useRef(mayArrangeIssue);
  mayArrangeIssueRef.current = mayArrangeIssue;
  const refresh = useCallback(() => {
    if (inFlight.current > 0) {
      staleWhileSaving.current = true;
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ["pm-issue-placements", buildingId] });
  }, [buildingId, queryClient]);

  useEffect(() => {
    if (!enabled) return;
    const handle = subscribeToIssuePlacements(buildingId, refresh);
    return () => handle.unsubscribe();
  }, [buildingId, enabled, refresh]);

  const bands = useMemo(() => indexPlacements(data), [data]);
  const ranksFor = useCallback((groupKey: string) => bands.get(groupKey), [bands]);

  const saveBand = useCallback(
    async (groupKey: string, visibleOrder: number[]) => {
      const ranks = bands.get(groupKey);
      const order = mergeArrangement(storedSequence(ranks), visibleOrder);
      // The merge carries ids the user cannot see — rows a filter hid, and rows
      // an issue left behind when it moved to another band — so the write can
      // reach issues the caller's permission check never covered. An upsert is
      // refused WHOLE by RLS, so one such row would fail a drop the UI allowed.
      // Those ids keep their slot in the numbering (that is what holds a
      // filtered view together) but are not written.
      const mayWrite = mayArrangeIssueRef.current;
      const changed = changedPositions(order, ranks).filter(
        (row) => !mayWrite || mayWrite(row.issue_id)
      );
      // Rows for these issues under ANOTHER status are left over from before
      // they moved: nothing renders them, every later save of that band renumbers
      // them again, and they carry the same refusal risk. Only status is cleared
      // — grouping by assignee or discipline puts one issue in several bands on
      // purpose.
      const arranged = new Set(order.filter((id) => !mayWrite || mayWrite(id)));
      const stale =
        groupBy === "status"
          ? data.filter((row) => row.group_key !== groupKey && arranged.has(row.issue_id))
          : EMPTY;
      if (changed.length === 0 && stale.length === 0) return;

      // Counted BEFORE the first await: a realtime echo arriving during it would
      // otherwise see no save in flight, invalidate, and land the rows from
      // before the drop on top of the optimistic ones.
      inFlight.current += 1;
      let previous: IssuePlacementRow[] | undefined;
      try {
        // Stop an in-flight read from landing on top of the optimistic rows with
        // the order from before the drop.
        await queryClient.cancelQueries({ queryKey });
        previous = queryClient.getQueryData<IssuePlacementRow[]>(queryKey);
        const moved = new Map(changed.map((row) => [row.issue_id, row.position]));
        const dropped = new Set(stale.map((row) => `${row.group_key}:${row.issue_id}`));
        queryClient.setQueryData<IssuePlacementRow[]>(queryKey, (rows = EMPTY) => [
          ...rows.filter(
            (row) =>
              !dropped.has(`${row.group_key}:${row.issue_id}`) &&
              (row.group_key !== groupKey || !moved.has(row.issue_id))
          ),
          ...changed.map((row) => ({ ...row, layout, group_by: groupBy, group_key: groupKey })),
        ]);

        await saveIssuePlacements(layout, groupBy, groupKey, changed);
        if (stale.length > 0) await deleteIssuePlacements(layout, groupBy, stale);
      } catch (e) {
        queryClient.setQueryData(queryKey, previous);
        toast.error(friendlyIssueError(e, t));
        // The toast is the only thing the user sees, and a refusal here is a
        // permission shape we cannot reproduce from a screenshot — an RLS
        // rejection because the band spans a project the user may not manage
        // looks identical to a dropped connection. Carry the band and the rows
        // so the refused write can be identified.
        console.error("[pm] issue placements save refused", {
          buildingId,
          layout,
          groupBy,
          groupKey,
          rows: changed.length,
          message: e instanceof Error ? e.message : String(e),
        });
        Sentry.captureException(e, {
          tags: { area: "pm", operation: "issue_placements_save", layout },
          extra: { buildingId, groupBy, groupKey, issueIds: changed.map((row) => row.issue_id) },
          fingerprint: ["pm", "issue-placements", "save", layout],
        });
        // The rollback may also have undone another save still in flight, so
        // settle on what the server holds once they are all done.
        staleWhileSaving.current = true;
      } finally {
        inFlight.current -= 1;
        if (inFlight.current === 0 && staleWhileSaving.current) {
          staleWhileSaving.current = false;
          refresh();
        }
      }
    },
    [bands, data, queryClient, queryKey, buildingId, layout, groupBy, t, refresh]
  );

  return { ranksFor, saveBand };
}
